import React, { useEffect, useRef, useState } from 'react';

const MeshNetwork = ({ mousePosition, scrollY }) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const nodesRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight * 1.5
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;

    const ctx = canvas.getContext('2d');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    // Initialize nodes
    const nodeCount = Math.floor(dimensions.width / 80);
    if (nodesRef.current.length === 0) {
      nodesRef.current = Array.from({ length: nodeCount }, () => ({
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        baseX: Math.random() * dimensions.width,
        baseY: Math.random() * dimensions.height,
        layer: Math.random() * 0.5 + 0.5
      }));
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw nodes
      nodesRef.current.forEach((node, i) => {
        // Drift movement
        node.x += node.vx;
        node.y += node.vy;

        // Boundary bounce
        if (node.x < 0 || node.x > dimensions.width) node.vx *= -1;
        if (node.y < 0 || node.y > dimensions.height) node.vy *= -1;

        // Mouse interaction
        if (mousePosition.x && mousePosition.y) {
          const dx = mousePosition.x - node.x;
          const dy = mousePosition.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 150) {
            const force = (150 - dist) / 150;
            node.x += dx * 0.001 * force * node.layer;
            node.y += dy * 0.001 * force * node.layer;
          }
        }

        // Draw connections
        nodesRef.current.forEach((otherNode, j) => {
          if (i >= j) return;
          
          const dx = node.x - otherNode.x;
          const dy = node.y - otherNode.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 200) {
            const opacity = (1 - dist / 200) * 0.15;
            const isNearMouse = mousePosition.x && 
              Math.abs(mousePosition.x - (node.x + otherNode.x) / 2) < 100 &&
              Math.abs(mousePosition.y - (node.y + otherNode.y) / 2) < 100;
            
            ctx.strokeStyle = isNearMouse 
              ? `rgba(77, 163, 255, ${opacity * 2})`
              : `rgba(47, 107, 255, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.stroke();
          }
        });

        // Draw node
        const isNearMouse = mousePosition.x && 
          Math.sqrt(
            Math.pow(mousePosition.x - node.x, 2) + 
            Math.pow(mousePosition.y - node.y, 2)
          ) < 100;
        
        ctx.fillStyle = isNearMouse 
          ? 'rgba(77, 163, 255, 0.8)'
          : 'rgba(47, 107, 255, 0.4)';
        ctx.beginPath();
        ctx.arc(node.x, node.y, isNearMouse ? 3 : 2, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions, mousePosition]);

  const opacity = scrollY > 100 ? Math.max(0, 1 - (scrollY - 100) / 500) : 1;

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 pointer-events-none"
      style={{
        opacity,
        transition: 'opacity 0.3s ease',
        zIndex: 0
      }}
    />
  );
};

export default MeshNetwork;
